/**
 * Form field JSX generation — dispatcher + per-type renderers
 */

import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import type { SchemaField } from '@core-engine/schema/types.js'
import { singularize, toPascalCase } from '@core-engine/utils/string.js'
import { renderListField } from '@next-adapter/generators/form/field-jsx-nested.js'
import {
  defaultValueFromHandlerName,
  fillControlClass,
  formItemProps,
  formLabel
} from '@next-adapter/generators/form/form-shared.js'
import { getFormFieldType } from '@next-adapter/generators/form/zod-schema.js'

export interface FieldRenderContext {
  creatableSelectFieldNames?: Set<string>
  defaultValueFromSourceNames?: Set<string>
  slotFilter?: 'main' | 'sidebar'
}

function defaultValueFromSourceHandler(
  field: SchemaField,
  context: FieldRenderContext
): string | null {
  if (!context.defaultValueFromSourceNames?.has(field.name)) return null
  return defaultValueFromHandlerName(field.name)
}

function generateIconPostfix(field: SchemaField, indent: string): string {
  const label = field.label || field.name
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}Icon"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem>
${indent}      ${formLabel(field, `${label} Icon`)}
${indent}      <FormControl>
${indent}        <IconPicker
${indent}          value={formField.value}
${indent}          onValueChange={formField.onChange}
${indent}          triggerPlaceholder="Select icon"
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function fillFormItemProps(field: SchemaField): string {
  return formItemProps(field, field.height === 'fill' ? 'flex flex-col' : undefined)
}

function formLabelWithDescription(
  field: SchemaField,
  label: string,
  hintJSX: string,
  indent: string
): string {
  if (!hintJSX) return formLabel(field, label)
  return `<div className="flex flex-col gap-1">
${indent}  ${formLabel(field, label)}
${indent}  ${hintJSX}
${indent}</div>`
}

export function generateFieldJSX(
  field: SchemaField,
  indent = '          ',
  context: FieldRenderContext = {}
): string {
  if (field.hidden) return ''
  if (context.slotFilter && (field.slot ?? 'main') !== context.slotFilter) return ''

  const mainJSX = generateFieldJSXCore(field, indent, context)
  if (!mainJSX) return ''

  if (field.hasIcon && !['group', 'section', 'tabs', 'separator'].includes(field.type)) {
    return `${mainJSX}\n${generateIconPostfix(field, indent)}`
  }

  return mainJSX
}

function generateFieldJSXCore(
  field: SchemaField,
  indent = '          ',
  context: FieldRenderContext = {}
): string {
  const fieldType = getFormFieldType(field)
  const label = field.label || field.name
  const hintJSX = field.hint ? `<FormDescription>${field.hint}</FormDescription>` : ''

  if ((field.type === 'group' || field.type === 'section') && field.fields)
    return renderGroupField(field, indent, label, context)
  if (field.type === 'separator') return renderSeparatorField(field, indent)
  if (field.type === 'boolean') return renderBooleanField(field, indent, label, hintJSX, context)
  if (field.type === 'image')
    return renderMediaField(field, indent, label, hintJSX, context, 'image/*')
  if (field.type === 'video')
    return renderMediaField(field, indent, label, hintJSX, context, 'video/*')
  if (field.type === 'media') return renderMediaField(field, indent, label, hintJSX, context)
  if (field.type === 'gallery') return renderGalleryField(field, indent, label, hintJSX, context)
  if (field.type === 'icon') return renderIconField(field, indent, label, hintJSX)
  if (field.type === 'date') return renderDateField(field, indent, label, hintJSX, context)
  if (field.type === 'select') return renderSelectField(field, indent, label, hintJSX, context)
  if (field.type === 'richtext') return renderRichtextField(field, indent, label, hintJSX, context)
  if (field.type === 'markdown') return renderMarkdownField(field, indent, label, hintJSX, context)
  if (field.type === 'text') return renderTextareaField(field, indent, label, hintJSX, context)
  if (field.type === 'number' || field.type === 'decimal')
    return renderNumberField(field, indent, label, fieldType, hintJSX, context)
  if (field.type === 'relationship' && field.relationship)
    return renderRelationshipField(field, indent, label, hintJSX, context)
  if (field.type === 'list') return renderListField(field, indent, label)

  return renderDefaultField(field, indent, label, fieldType, hintJSX, context)
}

function renderGroupField(
  field: SchemaField,
  indent: string,
  label: string,
  context: FieldRenderContext
): string {
  const columns = field.columns || 1
  const groupFields = field
    .fields!.map((nf) => generateFieldJSX(nf, `${indent}    `, context))
    .filter(Boolean)
    .join('\n')
  if (!groupFields) return ''
  const heading =
    label && label !== field.name
      ? `${indent}<h3 className="text-lg font-medium">${label}</h3>\n`
      : ''
  return `
${heading}${indent}  <div className="grid ${columns > 1 ? `grid-cols-${columns}` : 'grid-cols-1'} gap-5">
${groupFields}
${indent}</div>`
}

function renderSeparatorField(field: SchemaField, indent: string): string {
  if (field.label) {
    return `${indent}<div className="relative my-4">
${indent}  <div className="absolute inset-0 flex items-center">
${indent}    <Separator className="w-full" />
${indent}  </div>
${indent}  <div className="relative flex justify-center text-xs uppercase">
${indent}    <span className="bg-background px-2 text-muted-foreground">${field.label}</span>
${indent}  </div>
${indent}</div>`
  }
  return `${indent}<Separator className="my-4" />`
}

function renderBooleanField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onCheckedChange = defaultValueFromHandler
    ? `(checked) => {
${indent}            ${defaultValueFromHandler}(checked)
${indent}            formField.onChange(checked)
${indent}          }`
    : 'formField.onChange'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field, 'flex flex-row items-start space-x-3 space-y-0')}>
${indent}      <FormControl>
${indent}        <Checkbox
${indent}          checked={formField.value}
${indent}          onCheckedChange={${onCheckedChange}}
${indent}          disabled={isPending}
${indent}        />
${indent}      </FormControl>
${indent}      <div className="flex flex-col gap-1">
${indent}        <FormLabel>${label}</FormLabel>${hintJSX ? `\n${indent}        ${hintJSX}` : ''}
${indent}      </div>
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderMediaField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext,
  accept?: string
): string {
  const acceptProp = accept ? `\n${indent}          accept="${accept}"` : ''
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onChange = defaultValueFromHandler
    ? `(value) => {
${indent}            ${defaultValueFromHandler}(value)
${indent}            formField.onChange(value)
${indent}          }`
    : 'formField.onChange'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <MediaField
${indent}          value={formField.value}
${indent}          onChange={${onChange}}
${indent}          onBlur={formField.onBlur}
${indent}          disabled={isPending}${acceptProp}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderGalleryField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const maxItemsProp = field.maxItems ? `\n${indent}          maxItems={${field.maxItems}}` : ''
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onChange = defaultValueFromHandler
    ? `(value) => {
${indent}            ${defaultValueFromHandler}(value)
${indent}            formField.onChange(value)
${indent}          }`
    : 'formField.onChange'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <GalleryField
${indent}          value={formField.value}
${indent}          onChange={${onChange}}
${indent}          disabled={isPending}${maxItemsProp}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderIconField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string
): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <IconPicker
${indent}          value={formField.value}
${indent}          onValueChange={formField.onChange}
${indent}          triggerPlaceholder="Select ${label.toLowerCase()}"
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderDateField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onChange = defaultValueFromHandler
    ? `(value) => {
${indent}            ${defaultValueFromHandler}(value)
${indent}            formField.onChange(value)
${indent}          }`
    : 'formField.onChange'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <DatePicker
${indent}          value={formField.value}
${indent}          onChange={${onChange}}
${indent}          disabled={isPending}
${indent}          placeholder="Select ${label.toLowerCase()}"
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderSelectField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  if (field.multiple) {
    return renderMultiSelectField(field, indent, label, hintJSX, context)
  }

  if (field.creatable && context.creatableSelectFieldNames?.has(field.name)) {
    return renderCreatableSelectField(field, indent, label, hintJSX, context)
  }

  const options = field.options || []
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onValueChange = defaultValueFromHandler
    ? `(value) => {
${indent}            ${defaultValueFromHandler}(value)
${indent}            formField.onChange(value)
${indent}          }`
    : 'formField.onChange'
  const placeholder = field.placeholder || `Select ${label.toLowerCase()}`
  const selectedLabelExpression = options.reduceRight(
    (fallback, option) =>
      `formField.value === ${JSON.stringify(option.value)} ? ${JSON.stringify(option.label)} : ${fallback}`,
    'undefined'
  )
  const validValuesExpression = `([${options.map((option) => JSON.stringify(option.value)).join(', ')}] as const as readonly string[])`
  const optionsJSX = options
    .map(
      (o) =>
        `${indent}          <SelectItem key="${o.value}" value="${o.value}">${o.label}</SelectItem>`
    )
    .join('\n')
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <Select
${indent}          value={typeof formField.value === "string" && ${validValuesExpression}.includes(formField.value) ? formField.value : undefined}
${indent}          onValueChange={${onValueChange}}
${indent}          disabled={isPending}
${indent}        >
${indent}          <SelectTrigger className="w-full">
${indent}            <span data-slot="select-value" className="pointer-events-none">
${indent}              {(${selectedLabelExpression}) ?? ${JSON.stringify(placeholder)}}
${indent}            </span>
${indent}          </SelectTrigger>
${indent}          <SelectContent position="popper" align="start">
${optionsJSX}
${indent}          </SelectContent>
${indent}        </Select>
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderMultiSelectField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const fieldPascal = toPascalCase(field.name)
  const isCreatable = field.creatable && context.creatableSelectFieldNames?.has(field.name)
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const optionsVar = isCreatable ? `${field.name}Options` : 'staticOptions'
  const staticOptions = `[${(field.options ?? [])
    .map(
      (option) =>
        `{ label: ${JSON.stringify(option.label)}, value: ${JSON.stringify(option.value)} }`
    )
    .join(', ')}]`
  const openVar = `${field.name}Open`
  const setOpenVar = `set${fieldPascal}Open`
  const searchVar = `${field.name}Search`
  const setSearchVar = `set${fieldPascal}Search`
  const pendingVar = `create${fieldPascal}OptionPending`
  const createHandler = `handleCreate${fieldPascal}Option`
  const placeholder = field.placeholder || `Select ${label.toLowerCase()}`
  const previewLimit = Math.max(1, Math.floor(field.previewLimit ?? 1))
  const lowerLabel = label.toLowerCase()
  const optionsDeclaration = isCreatable
    ? `const options = ${optionsVar}`
    : `const staticOptions = ${staticOptions}
${indent}    const options = staticOptions`
  const searchInput = isCreatable
    ? `<CommandInput
${indent}                placeholder="Search ${lowerLabel}..."
${indent}                value={${searchVar}}
${indent}                onValueChange={${setSearchVar}}
${indent}              />`
    : `<CommandInput placeholder="Search ${lowerLabel}..." />`
  const createState = isCreatable
    ? `
${indent}    const createLabel = ${searchVar}.trim()
${indent}    const canCreateOption =
${indent}      createLabel.length > 0 &&
${indent}      !options.some(
${indent}        (option) =>
${indent}          option.label.toLowerCase() === createLabel.toLowerCase() ||
${indent}          option.value.toLowerCase() === createLabel.toLowerCase()
${indent}      )
`
    : ''
  const createAction = isCreatable
    ? `
${indent}              {canCreateOption ? (
${indent}                <>
${indent}                  <CommandSeparator />
${indent}                  <div className="p-1">
${indent}                    <Button
${indent}                      type="button"
${indent}                      variant="ghost"
${indent}                      className="w-full justify-start"
${indent}                      disabled={${pendingVar}}
${indent}                      onClick={() => ${createHandler}(createLabel)}
${indent}                    >
${indent}                      <Plus className="size-4" />
${indent}                      {${pendingVar} ? 'Creating...' : \`Create "\${createLabel}"\`}
${indent}                    </Button>
${indent}                  </div>
${indent}                </>
${indent}              ) : null}`
    : ''
  const clearSearch = isCreatable ? `\n${indent}                        ${setSearchVar}('')` : ''

  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => {
${indent}    ${optionsDeclaration}
${indent}    const selectedValues = Array.isArray(formField.value) ? formField.value : []
${indent}    const selectedOptions = options.filter((option) => selectedValues.includes(option.value))${createState}
${indent}    const previewLimit = ${previewLimit}
${indent}    const shouldCollapseSelectedOptions =
${indent}      selectedOptions.length > Math.max(previewLimit, 2)
${indent}    const visibleSelectedOptions = shouldCollapseSelectedOptions
${indent}      ? selectedOptions.slice(0, previewLimit)
${indent}      : selectedOptions
${indent}    const hiddenSelectedOptions = shouldCollapseSelectedOptions
${indent}      ? selectedOptions.slice(previewLimit)
${indent}      : []
${indent}    return (
${indent}      <FormItem${formItemProps(field)}>
${indent}        ${formLabelWithDescription(field, label, hintJSX, `${indent}        `)}
${indent}        <Popover open={${openVar}} onOpenChange={${setOpenVar}}>
${indent}          <PopoverTrigger asChild>
${indent}            <FormControl>
${indent}              <Button
${indent}                type="button"
${indent}                variant="outline"
${indent}                role="combobox"
${indent}                aria-expanded={${openVar}}
${indent}                className={cn("w-full justify-between gap-2", {
${indent}                  "text-muted-foreground": selectedOptions.length === 0
${indent}                })}
${indent}                disabled={isPending}
${indent}              >
${indent}                <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
${indent}                  {selectedOptions.length > 0 ? (
${indent}                    <>
${indent}                      <span className="truncate">
${indent}                        {visibleSelectedOptions.map((option) => option.label).join(', ')}
${indent}                      </span>
${indent}                      {hiddenSelectedOptions.length > 0 ? (
${indent}                        <Tooltip>
${indent}                          <TooltipTrigger asChild>
${indent}                            <Badge
${indent}                              variant="outline"
${indent}                              className="h-5 shrink-0 tabular-nums"
${indent}                            >
${indent}                              {'+'}
${indent}                              {hiddenSelectedOptions.length}{' '}
${indent}                              {hiddenSelectedOptions.length === 1 ? 'other' : 'others'}
${indent}                            </Badge>
${indent}                          </TooltipTrigger>
${indent}                          <TooltipContent align="center" side="bottom" className="max-w-60 w-full">
${indent}                            <div className="space-y-1">
${indent}                              {hiddenSelectedOptions.map((option) => (
${indent}                                <div key={option.value} className="truncate">
${indent}                                  {option.label}
${indent}                                </div>
${indent}                              ))}
${indent}                            </div>
${indent}                          </TooltipContent>
${indent}                        </Tooltip>
${indent}                      ) : null}
${indent}                    </>
${indent}                  ) : (
${indent}                    <span className="truncate">{${JSON.stringify(placeholder)}}</span>
${indent}                  )}
${indent}                </span>
${indent}                <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
${indent}              </Button>
${indent}            </FormControl>
${indent}          </PopoverTrigger>
${indent}          <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
${indent}            <Command>
${indent}              ${searchInput}
${indent}              <CommandList>
${indent}                <CommandEmpty>No ${lowerLabel} found.</CommandEmpty>
${indent}                <CommandGroup>
${indent}                  {options.map((option) => {
${indent}                    const isSelected = selectedValues.includes(option.value)
${indent}                    return (
${indent}                      <CommandItem
${indent}                        key={option.value}
${indent}                        value={\`\${option.label} \${option.value}\`}
${indent}                        onSelect={() => {
${indent}                          const nextValues = isSelected
${indent}                            ? selectedValues.filter((value: string) => value !== option.value)
${indent}                            : [...selectedValues, option.value]
${defaultValueFromHandler ? `${indent}                          ${defaultValueFromHandler}(nextValues)\n` : ''}${indent}                          formField.onChange(nextValues)${clearSearch}
${indent}                        }}
${indent}                      >
${indent}                        <Check className={cn("size-4", {
${indent}                          "opacity-100": isSelected,
${indent}                          "opacity-0": !isSelected
${indent}                        })} />
${indent}                        <span className="truncate">{option.label}</span>
${indent}                      </CommandItem>
${indent}                    )
${indent}                  })}
${indent}                </CommandGroup>
${indent}              </CommandList>${createAction}
${indent}            </Command>
${indent}          </PopoverContent>
${indent}        </Popover>
${indent}        <FormMessage />
${indent}      </FormItem>
${indent}    )
${indent}  }}
${indent}/>`
}

function renderCreatableSelectField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const fieldPascal = toPascalCase(field.name)
  const optionsVar = `${field.name}Options`
  const openVar = `${field.name}Open`
  const setOpenVar = `set${fieldPascal}Open`
  const searchVar = `${field.name}Search`
  const setSearchVar = `set${fieldPascal}Search`
  const pendingVar = `create${fieldPascal}OptionPending`
  const createHandler = `handleCreate${fieldPascal}Option`
  const placeholder = field.placeholder || `Select ${label.toLowerCase()}`
  const lowerLabel = label.toLowerCase()
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)

  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => {
${indent}    const selectedOption = ${optionsVar}.find((option) => option.value === formField.value)
${indent}    const createLabel = ${searchVar}.trim()
${indent}    const canCreateOption =
${indent}      createLabel.length > 0 &&
${indent}      !${optionsVar}.some(
${indent}        (option) =>
${indent}          option.label.toLowerCase() === createLabel.toLowerCase() ||
${indent}          option.value.toLowerCase() === createLabel.toLowerCase()
${indent}      )

${indent}    return (
${indent}      <FormItem${formItemProps(field)}>
${indent}        ${formLabelWithDescription(field, label, hintJSX, `${indent}        `)}
${indent}        <Popover open={${openVar}} onOpenChange={${setOpenVar}}>
${indent}          <PopoverTrigger asChild>
${indent}            <FormControl>
${indent}              <Button
${indent}                type="button"
${indent}                variant="outline"
${indent}                role="combobox"
${indent}                aria-expanded={${openVar}}
${indent}                className={cn("w-full justify-between", {
${indent}                  "text-muted-foreground": !selectedOption
${indent}                })}
${indent}                disabled={isPending}
${indent}              >
${indent}                <span className="truncate">{selectedOption?.label ?? ${JSON.stringify(placeholder)}}</span>
${indent}                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
${indent}              </Button>
${indent}            </FormControl>
${indent}          </PopoverTrigger>
${indent}          <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
${indent}            <Command>
${indent}              <CommandInput
${indent}                placeholder="Search ${lowerLabel}..."
${indent}                value={${searchVar}}
${indent}                onValueChange={${setSearchVar}}
${indent}              />
${indent}              <CommandList>
${indent}                <CommandEmpty>No ${lowerLabel} found.</CommandEmpty>
${indent}                <CommandGroup>
${indent}                  {${optionsVar}.map((option) => (
${indent}                    <CommandItem
${indent}                      key={option.value}
${indent}                      value={\`\${option.label} \${option.value}\`}
${indent}                      onSelect={() => {
${defaultValueFromHandler ? `${indent}                        ${defaultValueFromHandler}(option.value)\n` : ''}${indent}                        formField.onChange(option.value)
${indent}                        ${setSearchVar}('')
${indent}                        ${setOpenVar}(false)
${indent}                      }}
${indent}                    >
${indent}                      <Check className={cn("size-4", {
${indent}                        "opacity-100": formField.value === option.value,
${indent}                        "opacity-0": formField.value !== option.value
${indent}                      })} />
${indent}                      <span className="truncate">{option.label}</span>
${indent}                    </CommandItem>
${indent}                  ))}
${indent}                </CommandGroup>
${indent}              </CommandList>
${indent}              {canCreateOption ? (
${indent}                <>
${indent}                  <CommandSeparator />
${indent}                  <div className="p-1">
${indent}                    <Button
${indent}                      type="button"
${indent}                      variant="ghost"
${indent}                      className="w-full justify-start"
${indent}                      disabled={${pendingVar}}
${indent}                      onClick={() => ${createHandler}(createLabel)}
${indent}                    >
${indent}                      <Plus className="size-4" />
${indent}                      {${pendingVar} ? 'Creating...' : \`Create "\${createLabel}"\`}
${indent}                    </Button>
${indent}                  </div>
${indent}                </>
${indent}              ) : null}
${indent}            </Command>
${indent}          </PopoverContent>
${indent}        </Popover>
${indent}        <FormMessage />
${indent}      </FormItem>
${indent}    )
${indent}  }}
${indent}/>`
}

function renderRichtextField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onChange = defaultValueFromHandler
    ? `(value) => {
${indent}            ${defaultValueFromHandler}(value)
${indent}            formField.onChange(value)
${indent}          }`
    : 'formField.onChange'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${fillFormItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <ContentEditor
${indent}          value={formField.value}
${indent}          onChange={${onChange}}
${indent}          defaultMode="rich"
${indent}          className="${fillControlClass(field, 'min-h-[300px]')}"
${indent}          placeholder="Enter ${label.toLowerCase()}"
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderMarkdownField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onChange = defaultValueFromHandler
    ? `(value) => {
${indent}            ${defaultValueFromHandler}(value)
${indent}            formField.onChange(value)
${indent}          }`
    : 'formField.onChange'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${fillFormItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <ContentEditor
${indent}          value={formField.value}
${indent}          onChange={${onChange}}
${indent}          defaultMode="source"
${indent}          className="${fillControlClass(field, 'min-h-[300px]')}"
${indent}          placeholder="Enter ${label.toLowerCase()}"
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderTextareaField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const fieldProps = defaultValueFromHandler
    ? `value={formField.value ?? ''}
${indent}          onChange={(event) => {
${indent}            ${defaultValueFromHandler}(event.target.value)
${indent}            formField.onChange(event)
${indent}          }}
${indent}          onBlur={formField.onBlur}
${indent}          name={formField.name}
${indent}          ref={formField.ref}`
    : '{...formField}'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${fillFormItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <Textarea
${indent}          placeholder="Enter ${label.toLowerCase()}"
${indent}          className="${fillControlClass(field, 'min-h-[80px]')}"
${indent}          disabled={isPending}
${indent}          ${fieldProps}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderRelationshipField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const relName = field.relationship!
  const relSingular = singularize(relName)
  const relPascal = toPascalCase(relSingular)
  const isUserRelationship = isUserRelationshipTarget(relName)
  const displayField = isUserRelationship
    ? `(('name' in item && item.name) || ('email' in item && item.email) || \`User \${item.id ?? 'Unknown'}\`) as string`
    : `(('name' in item && item.name) || ('title' in item && item.title) || ('label' in item && item.label) || \`${relPascal} \${item.id ?? 'Unknown'}\`) as string`
  const secondaryDisplayField = isUserRelationship
    ? `('email' in item && item.email && item.email !== displayName ? String(item.email) : null)`
    : 'null'
  const dataArray = `${relName}Data?.${relName}`

  if (field.multiple) {
    return renderMultiRelationshipField(
      field,
      indent,
      label,
      hintJSX,
      displayField,
      secondaryDisplayField,
      dataArray,
      relSingular,
      context
    )
  }

  return renderSingleRelationshipField(
    field,
    indent,
    label,
    hintJSX,
    displayField,
    secondaryDisplayField,
    dataArray,
    relSingular,
    context
  )
}

function renderMultiRelationshipField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  displayField: string,
  secondaryDisplayField: string,
  dataArray: string,
  relSingular: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field, 'flex flex-col')}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <Popover open={${field.name}Open} onOpenChange={set${toPascalCase(field.name)}Open}>
${indent}        <PopoverTrigger asChild>
${indent}          <FormControl>
${indent}            <Button
${indent}              variant="outline"
${indent}              role="combobox"
${indent}              className={cn("w-full justify-between", {
${indent}                "text-muted-foreground": !formField.value || formField.value.length === 0
${indent}              })}
${indent}              disabled={isPending}
${indent}            >
${indent}              {(formField.value?.length ?? 0) > 0
${indent}                ? \`\${formField.value?.length ?? 0} selected\`
${indent}                : "Select ${label.toLowerCase()}"}
${indent}              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
${indent}            </Button>
${indent}          </FormControl>
${indent}        </PopoverTrigger>
${indent}        <PopoverContent className="w-full p-0" align="start">
${indent}          <Command>
${indent}            <CommandInput placeholder="Search ${label.toLowerCase()}..." />
${indent}            <CommandList>
${indent}              <CommandEmpty>No ${relSingular} found.</CommandEmpty>
${indent}              <CommandGroup>
${indent}                {${dataArray}?.filter((item) => item.id !== null).map((item) => {
${indent}                  const displayName = ${displayField}
${indent}                  const secondaryDisplayName = ${secondaryDisplayField}
${indent}                  const isSelected = formField.value?.includes(item.id) || false
${indent}                  return (
${indent}                    <CommandItem
${indent}                      key={item.id}
${indent}                      value={displayName}
${indent}                      onSelect={() => {
${indent}                        const current = formField.value || []
${defaultValueFromHandler ? `${indent}                        const nextValue = isSelected\n${indent}                          ? current.filter((id: string) => id !== item.id)\n${indent}                          : [...current, item.id]\n${indent}                        ${defaultValueFromHandler}(nextValue)\n${indent}                        formField.onChange(nextValue)` : `${indent}                        formField.onChange(isSelected\n${indent}                          ? current.filter((id: string) => id !== item.id)\n${indent}                          : [...current, item.id])`}
${indent}                      }}
${indent}                    >
${indent}                      <Check className={cn("size-4", {
${indent}                        "opacity-100": isSelected,
${indent}                        "opacity-0": !isSelected
${indent}                      })} />
${indent}                      <span className="flex min-w-0 flex-col text-left">
${indent}                        <span className="truncate">{displayName}</span>
${indent}                        {secondaryDisplayName ? (
${indent}                          <span className="truncate text-xs text-muted-foreground">{secondaryDisplayName}</span>
${indent}                        ) : null}
${indent}                      </span>
${indent}                    </CommandItem>
${indent}                  )
${indent}                })}
${indent}              </CommandGroup>
${indent}            </CommandList>
${indent}          </Command>
${indent}        </PopoverContent>
${indent}      </Popover>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderSingleRelationshipField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  displayField: string,
  secondaryDisplayField: string,
  dataArray: string,
  relSingular: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field, 'flex flex-col')}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <Popover open={${field.name}Open} onOpenChange={set${toPascalCase(field.name)}Open}>
${indent}        <PopoverTrigger asChild>
${indent}          <FormControl>
${indent}            <Button
${indent}              variant="outline"
${indent}              role="combobox"
${indent}              className={cn("w-full justify-between", {
${indent}                "text-muted-foreground": !formField.value
${indent}              })}
${indent}              disabled={isPending}
${indent}            >
${indent}              {formField.value
${indent}                ? (() => {
${indent}                    const item = ${dataArray}?.find((item) => item.id !== null && String(item.id) === formField.value)
${indent}                    return item ? (${displayField}) : "Select ${label.toLowerCase()}"
${indent}                  })()
${indent}                : "Select ${label.toLowerCase()}"}
${indent}              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
${indent}            </Button>
${indent}          </FormControl>
${indent}        </PopoverTrigger>
${indent}        <PopoverContent className="w-full p-0" align="start">
${indent}          <Command>
${indent}            <CommandInput placeholder="Search ${label.toLowerCase()}..." />
${indent}            <CommandList>
${indent}              <CommandEmpty>No ${relSingular} found.</CommandEmpty>
${indent}              <CommandGroup>
${indent}                {${dataArray}?.filter((item) => item.id !== null).map((item) => {
${indent}                  const displayName = ${displayField}
${indent}                  const secondaryDisplayName = ${secondaryDisplayField}
${indent}                  return (
${indent}                    <CommandItem
${indent}                      key={item.id}
${indent}                      value={displayName}
${indent}                      onSelect={() => {
${defaultValueFromHandler ? `${indent}                        ${defaultValueFromHandler}(String(item.id))\n` : ''}${indent}                        formField.onChange(String(item.id))
${indent}                        set${toPascalCase(field.name)}Open(false)
${indent}                      }}
${indent}                    >
${indent}                      <Check className={cn("size-4", {
${indent}                        "opacity-100": formField.value === String(item.id),
${indent}                        "opacity-0": formField.value !== String(item.id)
${indent}                      })} />
${indent}                      <span className="flex min-w-0 flex-col text-left">
${indent}                        <span className="truncate">{displayName}</span>
${indent}                        {secondaryDisplayName ? (
${indent}                          <span className="truncate text-xs text-muted-foreground">{secondaryDisplayName}</span>
${indent}                        ) : null}
${indent}                      </span>
${indent}                    </CommandItem>
${indent}                  )
${indent}                })}
${indent}              </CommandGroup>
${indent}            </CommandList>
${indent}          </Command>
${indent}        </PopoverContent>
${indent}      </Popover>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderDefaultField(
  field: SchemaField,
  indent: string,
  label: string,
  fieldType: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const fieldProps = defaultValueFromHandler
    ? `value={formField.value ?? ''}
${indent}          onChange={(event) => {
${indent}            ${defaultValueFromHandler}(event.target.value)
${indent}            formField.onChange(event)
${indent}          }}
${indent}          onBlur={formField.onBlur}
${indent}          name={formField.name}
${indent}          ref={formField.ref}`
    : '{...formField}'
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <Input
${indent}          type="${fieldType}"
${indent}          placeholder="Enter ${label.toLowerCase()}"
${indent}          disabled={isPending}
${indent}          ${fieldProps}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderNumberField(
  field: SchemaField,
  indent: string,
  label: string,
  fieldType: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const stepProp = field.type === 'decimal' ? `\n${indent}          step="any"` : ''
  const emptyValue = field.required ? 'undefined' : 'null'
  const applyDefaultValueFrom = defaultValueFromHandler
    ? `${indent}            ${defaultValueFromHandler}(normalizedValue)\n`
    : ''

  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${field.name}"
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${formLabelWithDescription(field, label, hintJSX, `${indent}      `)}
${indent}      <FormControl>
${indent}        <Input
${indent}          type="${fieldType}"${stepProp}
${indent}          placeholder="Enter ${label.toLowerCase()}"
${indent}          disabled={isPending}
${indent}          value={formField.value ?? ''}
${indent}          onChange={(event) => {
${indent}            const nextValue = event.target.value === '' ? ${emptyValue} : event.target.valueAsNumber
${indent}            const normalizedValue = Number.isNaN(nextValue) ? ${emptyValue} : nextValue
${applyDefaultValueFrom}${indent}            formField.onChange(normalizedValue)
${indent}          }}
${indent}          onBlur={formField.onBlur}
${indent}          name={formField.name}
${indent}          ref={formField.ref}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}
